import type { AxiosResponse } from 'axios';
import Axios from 'axios';
import { nextTick } from 'vue';
import type { AppConfigType } from '../api/app-config';
import { elixirConfig } from '../legacy/lib/config/elixir-config';
import { appConfig } from '../legacy/lib/config/app-config';
import { accountTags } from '../legacy/lib/config/account-tags';
import { templateVariables } from '../legacy/lib/config/template-variables';
import { XSRFHelper } from '../legacy/lib/util/xsrf-helper';
import { FeatureFlagsAPI } from '../legacy/api/feature-flags';
import { featureFlags } from './feature-flags/feature-flags';

type AppInitDataType = {
  config: AppConfigType;
};

export type BootsrapOptionsType = {
  // ожидание пока кто-то другой не загрузит
  wait: boolean;
  skip: boolean;
};

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    __BOOTSTRAP__: {
      done: boolean | undefined;
      promise: Promise<AppInitDataType>;
      resolve: (data: AppInitDataType) => void;
    };
  }
}

if (!window.__BOOTSTRAP__) {
  let resolve: (data: AppInitDataType) => void;
  const promise = new Promise<AppInitDataType>((resolve_) => {
    resolve = resolve_;
  });
  window.__BOOTSTRAP__ = {
    done: undefined,
    promise,
    // @ts-expect-error it's ok
    resolve
  };
}

export async function bootstrap() {
  if (window.__BOOTSTRAP__.done !== undefined) {
    return window.__BOOTSTRAP__.promise;
  }

  window.__BOOTSTRAP__.done = false;
  const accountNick = appConfig.get('accountNick');
  if (!accountNick) {
    nextTick(() => {
      window.__BOOTSTRAP__.resolve({
        config: appConfig.config.value
      });
      window.__BOOTSTRAP__.done = true;
    });

    return window.__BOOTSTRAP__.promise;
  }

  const axios = getAxios();
  const getResponseData = <T>(resp: AxiosResponse<T>) => resp.data;

  const accountTagsPromise = axios
    .get(`/app/api/my/${accountNick}/tag`)
    .then((resp: AxiosResponse<{ items: [] }>) => resp.data.items)
    .catch(() => []);

  const templateVariablesPromise = axios
    .get('/app/api/dict/template/variables')
    .then((resp: AxiosResponse<{ items: [] }>) => resp.data.items)
    .catch(() => []);

  const flagsPromise = FeatureFlagsAPI.fetchFeatureFlags()
    .then(({ toggles }) => toggles)
    .catch(() => []);

  const [accountTagsData, templateVariablesData, flags] = await Promise.all([
    accountTagsPromise,
    templateVariablesPromise,
    flagsPromise
  ]);

  featureFlags.setToggles(flags);

  accountTags.setAccountTags(accountTagsData);
  templateVariables.setTemplateVariables(templateVariablesData);

  const elixirConfigPromise = axios
    .get(`/app/api/my/${accountNick}/elixir_init`)
    .then(getResponseData)
    .catch(() => null);
  const elixir = await elixirConfigPromise;
  elixirConfig.setElixirConfig(elixir);

  nextTick(() => {
    window.__BOOTSTRAP__.resolve({
      config: appConfig.config.value
    });
    window.__BOOTSTRAP__.done = true;
  });

  return window.__BOOTSTRAP__.promise;
}

export function waitBootstrap() {
  return window.__BOOTSTRAP__.promise;
}

function getAxios() {
  const axios = Axios.create();
  axios.defaults.headers.common['X-XSRFToken'] = XSRFHelper.getToken();
  axios.defaults.headers.common.Accept = 'application/json';
  return axios;
}
